import type { ReportCustomerDto, Cadence } from '@act/shared/data-transfer-objects';
import type { PreviewSection } from '../utils/reportDisplay.util';
import { toReadingColumnItemWithSnapshot } from '../preview/reportPreview.adapter.util';
import { toMockSectionCustomerInput } from '../utils/resolveSectionSnapshot.util';
import { reportSpriteHtml } from '../preview/sections/reportBodyHtml.util';
import { ReportPreviewShell } from '../preview/ReportPreviewShell';
import { ReportSectionItemView } from '../preview/ReportSectionItemView';
import type {
  ReportInsightCardKey,
  ReportInsightFieldKey,
} from '../preview/ReportInsightPair';

export interface ReportSectionPreviewProps {
  section: PreviewSection;
  /** Customer context for draft mock snapshots when `dataSnapshot` is empty. */
  customer?: ReportCustomerDto | null;
  /** Reporting period label paired with customer for mock snapshots. */
  reportingPeriod?: string | null;
  measurementPeriod?: string | null;
  reportId?: string;
  cadence?: Cadence;
  /** When true, callout is edited inline in the preview. */
  authoringEditable?: boolean;
  /** @param value - Callout text after inline edit. */
  onCalloutBlur?: (value: string) => void;
  /** @param value - Callout title after inline edit. */
  onCalloutTitleBlur?: (value: string) => void;
  /**
   * @param dataSnapshot - Resolved snapshot used for insight defaults.
   * @param card - Clinical or financial insight card.
   * @param field - Heading or body field.
   * @param value - Updated insight text while typing.
   */
  onInsightFieldChange?: (
    dataSnapshot: Record<string, unknown> | null,
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
  /**
   * @param dataSnapshot - Resolved snapshot used for insight defaults.
   * @param card - Clinical or financial insight card.
   * @param field - Heading or body field.
   * @param value - Final insight text after blur.
   */
  onInsightFieldBlur?: (
    dataSnapshot: Record<string, unknown> | null,
    card: ReportInsightCardKey,
    field: ReportInsightFieldKey,
    value: string,
  ) => void;
}

/**
 * Read-only preview block for one included report section (same render path as full preview).
 * @param props - Draft row or published snapshot section plus customer context.
 */
const ReportSectionPreview = ({
  section,
  customer = null,
  reportingPeriod = null,
  measurementPeriod = null,
  reportId,
  cadence,
  authoringEditable = false,
  onCalloutBlur,
  onCalloutTitleBlur,
  onInsightFieldChange,
  onInsightFieldBlur,
}: ReportSectionPreviewProps) => {
  if (!customer || !reportingPeriod) {
    return (
      <ReportPreviewShell>
        <p className="text-sm text-muted-foreground">Loading preview data…</p>
      </ReportPreviewShell>
    );
  }

  const resolvedMeasurementPeriod = measurementPeriod ?? reportingPeriod;

  const item = toReadingColumnItemWithSnapshot(
    section,
    customer,
    reportingPeriod,
    resolvedMeasurementPeriod ?? undefined,
  );
  const customerInput = toMockSectionCustomerInput(customer);

  return (
    <ReportPreviewShell>
      <main className="reema-report report-theme">
        <div
          aria-hidden
          dangerouslySetInnerHTML={{ __html: reportSpriteHtml }}
        />
        <ReportSectionItemView
          key={section.id}
          item={item}
          customer={customerInput}
          reportingPeriod={reportingPeriod}
          measurementPeriod={resolvedMeasurementPeriod ?? undefined}
          mode="preview"
          reportId={reportId}
          cadence={cadence}
          authoringEditable={authoringEditable}
          onCalloutChange={onCalloutBlur}
          onCalloutTitleChange={onCalloutTitleBlur}
          onInsightFieldChange={
            onInsightFieldChange
              ? (card, field, value) =>
                  onInsightFieldChange(item.dataSnapshot, card, field, value)
              : undefined
          }
          onInsightFieldBlur={
            onInsightFieldBlur
              ? (card, field, value) =>
                  onInsightFieldBlur(item.dataSnapshot, card, field, value)
              : undefined
          }
        />
      </main>
    </ReportPreviewShell>
  );
};

export default ReportSectionPreview;
